import React, { useEffect, useMemo, useState } from "react";
import {
  listAllDashboards,
  listAccessUsers,
  grantDashboardAccess,
  denyDashboardAccess,
  revokeDashboardAccess,
  listAccessGrants,
  setUserRole,
} from "../api/client";

/**
 * Admin-only page: manage user roles + explicit dashboard access overrides.
 *
 * Model (backend source of truth):
 *   Role defaults = implicit dashboards per role (admin→all, c_level→main+
 *   leadership+organization, manager→main+my_team, employee→none).
 *   Explicit overrides in the DashboardAccess table layer on top:
 *     allow → adds a dashboard not in role default
 *     deny  → removes a dashboard a role default would grant
 *   Effective = (role_defaults ∪ allows) - denies.
 *
 * Per-dashboard button logic:
 *   no explicit | role grants      → "Deny"   (override the default off)
 *   no explicit | role denies      → "Grant"  (add access)
 *   explicit allow                 → "Revoke" (falls back to role default)
 *   explicit deny                  → "Revoke" (falls back to role default)
 *
 * This means clicking Revoke/Deny on a dashboard ALWAYS changes the effective
 * access — which is what admins expect when they click the button.
 */
export default function AccessControl() {
  const [users, setUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [grants, setGrants] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [u, d, g] = await Promise.all([
        listAccessUsers(),
        listAllDashboards(),
        listAccessGrants(),
      ]);
      setUsers(u.data || []);
      setDashboards(d.data?.dashboards || []);
      setGrants(g.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load access data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedUser = useMemo(
    () => users.find((u) => u.user_id === selectedUserId) || null,
    [users, selectedUserId]
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s) ||
        (u.business_unit || "").toLowerCase().includes(s)
    );
  }, [users, search]);

  const grantsForUser = useMemo(() => {
    if (!selectedUser) return [];
    return grants.filter((g) => g.user_id === selectedUser.user_id);
  }, [grants, selectedUser]);

  const handleGrant = async (dashboardKey) => {
    if (!selectedUser || busy) return;
    setBusy(true);
    try {
      await grantDashboardAccess({
        user_id: selectedUser.user_id,
        dashboard_key: dashboardKey,
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Grant failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDeny = async (dashboardKey) => {
    if (!selectedUser || busy) return;
    const isSelf = currentUserIsSelected(selectedUser);
    const msg = isSelf
      ? "This will revoke YOUR access to this dashboard. Continue?"
      : `Deny ${selectedUser.name || selectedUser.email} access to this dashboard?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await denyDashboardAccess({
        user_id: selectedUser.user_id,
        dashboard_key: dashboardKey,
      });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Deny failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (grantId) => {
    if (busy) return;
    if (!window.confirm("Remove this explicit rule? Access falls back to the role default.")) return;
    setBusy(true);
    try {
      await revokeDashboardAccess(grantId);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Revoke failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRoleChange = async (role) => {
    if (!selectedUser || busy) return;
    if (!window.confirm(`Change ${selectedUser.name}'s role to ${role}?`)) return;
    setBusy(true);
    try {
      await setUserRole(selectedUser.user_id, role);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Role change failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }} className="aiq-muted">Loading access control…</div>;
  if (error) return <div style={{ padding: 24, color: "var(--color-incorrect)" }}>{error}</div>;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ marginTop: 0 }}>Access control</h1>
      <p className="aiq-muted" style={{ marginTop: -4 }}>
        Each role grants default dashboards. Explicit overrides layer on top:
        <strong> Grant</strong> adds access, <strong>Deny</strong> removes it
        even when the role would allow, <strong>Revoke</strong> removes the
        explicit rule so access falls back to the role default.
      </p>

      <div className="aiq-two-col" style={{ marginTop: 16 }}>
        {/* Users list */}
        <div className="aiq-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: 12, borderBottom: "1px solid var(--border-subtle)" }}>
            <label htmlFor="access-search" className="aiq-label" style={{ marginTop: 0 }}>
              Search users
            </label>
            <input
              id="access-search"
              className="aiq-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, or business unit…"
              aria-label="Search users by name, email, or business unit"
            />
            <div style={{ marginTop: 6, fontSize: 12 }} className="aiq-muted">
              {filtered.length} of {users.length} users
            </div>
          </div>
          <div style={{ maxHeight: 600, overflowY: "auto" }} role="listbox" aria-label="Users">
            {filtered.map((u) => (
              <button
                type="button"
                key={u.user_id}
                role="option"
                aria-selected={u.user_id === selectedUserId}
                onClick={() => setSelectedUserId(u.user_id)}
                className={
                  "aiq-user-row" +
                  (u.user_id === selectedUserId ? " aiq-selected" : "")
                }
              >
                <div style={{ fontWeight: 600 }}>{u.name || "(no name)"}</div>
                <div style={{ fontSize: 12 }} className="aiq-muted">{u.email}</div>
                <div style={{ marginTop: 4 }}>
                  <RoleChip role={u.role} isAdmin={u.is_admin} />
                  {u.explicit_grants?.length > 0 && (
                    <span className="aiq-chip aiq-chip-neutral" title="Explicit allow rows">
                      +{u.explicit_grants.length} grant
                      {u.explicit_grants.length > 1 ? "s" : ""}
                    </span>
                  )}
                  {u.explicit_denies?.length > 0 && (
                    <span className="aiq-chip aiq-chip-neutral" style={{ background: "rgba(239,68,68,0.15)", color: "var(--color-incorrect)" }} title="Explicit deny rows">
                      −{u.explicit_denies.length} den{u.explicit_denies.length > 1 ? "ies" : "y"}
                    </span>
                  )}
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: 16 }} className="aiq-muted">No users match.</div>
            )}
          </div>
        </div>

        {/* Detail panel */}
        <div>
          {!selectedUser ? (
            <div
              className="aiq-card aiq-muted"
              style={{ padding: 32, borderStyle: "dashed", textAlign: "center" }}
            >
              Select a user to manage their access.
            </div>
          ) : (
            <div className="aiq-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: 16, borderBottom: "1px solid var(--border-subtle)" }}>
                <h2 style={{ margin: 0 }}>{selectedUser.name || selectedUser.email}</h2>
                <div style={{ fontSize: 13, marginTop: 4 }} className="aiq-muted">
                  {selectedUser.email}
                  {selectedUser.designation ? ` • ${selectedUser.designation}` : ""}
                  {selectedUser.business_unit ? ` • ${selectedUser.business_unit}` : ""}
                </div>
              </div>

              <section style={{ padding: 16, borderBottom: "1px solid var(--border-subtle)" }}>
                <h3 className="aiq-section-head">Role</h3>
                <div
                  style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}
                  role="radiogroup"
                  aria-label="User role"
                >
                  {["employee", "manager", "senior_leader", "c_level", "admin", "superadmin"].map((role) => {
                    const active =
                      role === selectedUser.role ||
                      (role === "admin" && selectedUser.is_admin);
                    return (
                      <button
                        type="button"
                        key={role}
                        role="radio"
                        aria-checked={active}
                        onClick={() => handleRoleChange(role)}
                        disabled={active || busy}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid",
                          borderColor: active ? "var(--color-highlight)" : "var(--border-subtle)",
                          background: active ? "var(--color-highlight)" : "transparent",
                          color: active ? "#fff" : "var(--text-primary)",
                          borderRadius: 16,
                          cursor: active ? "default" : "pointer",
                          opacity: busy ? 0.6 : 1,
                          fontFamily: "inherit",
                        }}
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section style={{ padding: 16 }}>
                <h3 className="aiq-section-head">Dashboard access</h3>
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table className="aiq-table">
                    <thead>
                      <tr>
                        <th>Dashboard</th>
                        <th>Role default</th>
                        <th>Status</th>
                        <th>Effective</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboards.map((d) => {
                        const explicit = grantsForUser.find(
                          (g) => g.dashboard_key === d.key
                        );
                        const explicitAction = explicit?.action || null; // 'allow' | 'deny' | null
                        const roleGrants =
                          selectedUser.role_defaults?.includes(d.key) ?? false;
                        const effective = selectedUser.effective_dashboards.includes(d.key);

                        let status;
                        if (explicitAction === "allow") status = "Explicit allow";
                        else if (explicitAction === "deny") status = "Explicit deny";
                        else status = "—";

                        return (
                          <tr key={d.key}>
                            <td>{d.label}</td>
                            <td>
                              {roleGrants ? (
                                <span className="aiq-chip aiq-chip-active">Allowed</span>
                              ) : (
                                <span className="aiq-chip aiq-chip-neutral">No</span>
                              )}
                            </td>
                            <td>{status}</td>
                            <td>
                              {effective ? (
                                <span className="aiq-chip aiq-chip-active">Active</span>
                              ) : (
                                <span className="aiq-chip aiq-chip-neutral">Blocked</span>
                              )}
                            </td>
                            <td>
                              <DashboardActions
                                dashboard={d}
                                user={selectedUser}
                                explicit={explicit}
                                roleGrants={roleGrants}
                                busy={busy}
                                onGrant={() => handleGrant(d.key)}
                                onDeny={() => handleDeny(d.key)}
                                onRevoke={() => handleRevoke(explicit.id)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Small helper: hint when admin is editing themselves. The warning message
 * in handleDeny uses the actual current user, but at the component level we
 * don't have it — so we fall back to a heuristic (the role-chip currently
 * rendered in the page is the same as the detail panel's). */
function currentUserIsSelected(u) {
  try {
    const stored = JSON.parse(localStorage.getItem("aiq_user") || "null");
    return stored?.id === u.user_id;
  } catch {
    return false;
  }
}

const DashboardActions = ({
  dashboard,
  user,
  explicit,
  roleGrants,
  busy,
  onGrant,
  onDeny,
  onRevoke,
}) => {
  const label = dashboard.label;
  const userLabel = user.name || user.email;
  if (explicit) {
    return (
      <button
        type="button"
        className="aiq-btn-danger"
        disabled={busy}
        onClick={onRevoke}
        aria-label={`Revoke explicit ${explicit.action} on ${label} for ${userLabel}`}
      >
        Revoke {explicit.action}
      </button>
    );
  }
  // No explicit rule — offer the inverse of the role default.
  if (roleGrants) {
    return (
      <button
        type="button"
        className="aiq-btn-danger"
        disabled={busy}
        onClick={onDeny}
        aria-label={`Deny ${label} for ${userLabel}`}
      >
        Deny
      </button>
    );
  }
  return (
    <button
      type="button"
      className="aiq-btn-primary"
      style={{ padding: "4px 10px" }}
      disabled={busy}
      onClick={onGrant}
      aria-label={`Grant ${label} to ${userLabel}`}
    >
      Grant
    </button>
  );
};

const RoleChip = ({ role, isAdmin }) => {
  const r = isAdmin ? "admin" : role || "employee";
  return <span className={`aiq-chip aiq-chip-role-${r}`}>{r}</span>;
};
